import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, Button } from '@mui/material';
import {
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Analytics as AnalyticsIcon,
  SupportAgent as SupportIcon
} from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import palette from 'src/theme/palette';
import { postSettingData } from 'src/ApiServices';
import { toast } from 'react-toastify';
import { InputAdornment } from '@mui/material';

const IntegrationsTab = ({ tabData = [], typeId }) => {
  // Initialize formData with id and value structure
  const getInitialFormData = () => {
    const dataMap = {};
    tabData.forEach((item) => {
      dataMap[item.key] = {
        id: item.id || '',
        value: item.value || ''
      };
    });

    return {
      call_now_button_number: dataMap['call_now_button_number'] || { id: '', value: '' },
      google_analytics_key: dataMap['google_analytics_key'] || { id: '', value: '' },
      whatsapp_chat_button: dataMap['whatsapp_chat_button'] || { id: '', value: '' },
      tawk_to_live_chat_key: dataMap['tawk_to_live_chat_key'] || { id: '', value: '' },
      whatsapp_default_messages: dataMap['whatsapp_default_messages'] || {
        id: '',
        value: ''
      }
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [loading, setLoading] = useState(false);
  // Update formData when tabData changes
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [tabData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);

    const valueArray = [
      {
        id: formData.call_now_button_number.id || null,
        key: 'call_now_button_number',
        value: formData.call_now_button_number.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Integrations',
        created_at: formData.call_now_button_number.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.google_analytics_key.id || null,
        key: 'google_analytics_key',
        value: formData.google_analytics_key.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Integrations',
        created_at: formData.google_analytics_key.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.whatsapp_chat_button.id || null,
        key: 'whatsapp_chat_button',
        value: formData.whatsapp_chat_button.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Integrations',
        created_at: formData.whatsapp_chat_button.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.tawk_to_live_chat_key.id || null,
        key: 'tawk_to_live_chat_key',
        value: formData.tawk_to_live_chat_key.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Integrations',
        created_at: formData.tawk_to_live_chat_key.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.whatsapp_default_messages.id || null,
        key: 'whatsapp_default_messages',
        value: formData.whatsapp_default_messages.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Integrations',
        created_at: formData.whatsapp_default_messages.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      }
    ];

    const finalPayload = {
      type_id: typeId.toString(),
      value: JSON.stringify(valueArray)
    };

    try {
      const response = await postSettingData(finalPayload);

      const message =
        response?.message || response?.data?.message || 'Integrations settings saved successfully!';

      toast.success(message, {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      console.error('Integrations Save Error:', error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to save integration settings. Please try again.';

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box component="form" noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Call Now Button Number"
            name="call_now_button_number"
            value={formData?.call_now_button_number?.value || ''}
            onChange={(e) => handleInputChange('call_now_button_number', e.target.value)}
            placeholder="Enter phone number for call button"
            InputProps={{
              startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="WhatsApp Chat Button"
            name="whatsapp_chat_button"
            value={formData?.whatsapp_chat_button?.value || ''}
            onChange={(e) => handleInputChange('whatsapp_chat_button', e.target.value)}
            placeholder="Enter WhatsApp number for chat"
            InputProps={{
              startAdornment: <ChatIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Google Analytics Key"
            name="google_analytics_key"
            value={formData?.google_analytics_key?.value || ''}
            onChange={(e) => handleInputChange('google_analytics_key', e.target.value)}
            placeholder="Enter Google Analytics tracking ID"
            InputProps={{
              startAdornment: <AnalyticsIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            helperText="Format: G-XXXXXXXXXX or UA-XXXXXXXXX-X"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tawk.to Live Chat Key"
            name="tawk_to_live_chat_key"
            value={formData?.tawk_to_live_chat_key?.value || ''}
            onChange={(e) => handleInputChange('tawk_to_live_chat_key', e.target.value)}
            placeholder="Enter your Tawk.to live chat key"
            InputProps={{
              startAdornment: <SupportIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            helperText="This key will be used to integrate live chat functionality"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Whatsapp Default Messages"
            name="whatsapp_default_messages"
            value={formData?.whatsapp_default_messages?.value || ''}
            onChange={(e) => handleInputChange('whatsapp_default_messages', e.target.value)}
            placeholder="Enter default messages for WhatsApp"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    alignSelf: 'flex-start',
                    mt: '14px'
                  }}
                >
                  <ChatIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} container justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IntegrationsTab;
